// Header.js - 모바일 호환성 개선
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './Header.css';

function Header() {
	const [isLoggedIn, setIsLoggedIn] = useState(false);
	const [username, setUsername] = useState('');
	const [cartCount, setCartCount] = useState(0);
	const [wishlistCount, setWishlistCount] = useState(0);
	const [searchTerm, setSearchTerm] = useState('');
	const [showSituationMode, setShowSituationMode] = useState(false);
	const [showCustomerMenu, setShowCustomerMenu] = useState(false);
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	// ✅ 모바일 호환 상단 이동 함수 (개선)
	const scrollToTop = useCallback(() => {
		// 방법 1: 기본
		window.scrollTo(0, 0);
		
		// 방법 2: smooth (일부 브라우저)
		try {
			window.scrollTo({
				top: 0,
				left: 0,
				behavior: 'instant' // 모바일에서는 instant가 더 안정적
			});
		} catch (e) {
			// 구형 브라우저 폴백
			window.scrollTo(0, 0);
		}
		
		// 방법 3: document 레벨 (iOS Safari 대응)
		document.body.scrollTop = 0;
		document.documentElement.scrollTop = 0;
		
		// 방법 4: 특정 요소가 있다면
		const mainContent = document.querySelector('.product-list') || 
		                   document.querySelector('.main-content') ||
		                   document.querySelector('#root');
		if (mainContent) {
			mainContent.scrollTop = 0;
		}
	}, []);

	useEffect(() => {
		checkLoginStatus();
		updateCartCount();
		
		const token = localStorage.getItem('access_token');
		if (token) {
			updateWishlistCount();
		}
	}, []);
	
	useEffect(() => {
		if (isLoggedIn) {
			updateWishlistCount();
			updateCartCount();
			window.dispatchEvent(new Event('shuffleProducts'));
		} else {
			setWishlistCount(0);
			setCartCount(0);
		}
	}, [isLoggedIn]);

	useEffect(() => {
		const search = searchParams.get('search');
		if (search) {
			setSearchTerm(search);
		}

		const handleStorageChange = () => {
			checkLoginStatus();
			const token = localStorage.getItem('access_token');
			if (token) {
				updateWishlistCount();
				updateCartCount();
			}
		};

		const handleLoginStateChange = () => {
			checkLoginStatus();
			
			const token = localStorage.getItem('access_token');
			if (token) {
				console.log('🔥 로그인 감지 - 이벤트 발생!');
				setTimeout(() => {
					updateWishlistCount();
					updateCartCount();
					window.dispatchEvent(new Event('shuffleProducts'));
					console.log('✅ shuffleProducts 이벤트 발생 완료');
				}, 100);
			}
		};

		const handleWishlistUpdate = () => {
			updateWishlistCount();
		};

		const handleCartUpdate = () => {
			updateCartCount();
		};

		window.addEventListener('storage', handleStorageChange);
		window.addEventListener('loginStateChanged', handleLoginStateChange);
		window.addEventListener('cartUpdated', handleCartUpdate);
		window.addEventListener('wishlistUpdated', handleWishlistUpdate);

		return () => {
			window.removeEventListener('storage', handleStorageChange);
			window.removeEventListener('loginStateChanged', handleLoginStateChange);
			window.removeEventListener('cartUpdated', handleCartUpdate);
			window.removeEventListener('wishlistUpdated', handleWishlistUpdate);
		};
	}, [searchParams]);

	const checkLoginStatus = () => {
		const token = localStorage.getItem('access_token');
		const userStr = localStorage.getItem('user');
		
		if (token && userStr) {
			setIsLoggedIn(true);
			try {
				const user = JSON.parse(userStr);
				
				let displayName = '';
				
				if (user.profile && user.profile.name) {
					displayName = user.profile.name;
				} else if (user.username) {
					displayName = user.username;
				} else if (user.email) {
					displayName = user.email.split('@')[0];
				} else {
					displayName = '사용자';
				}
				
				setUsername(displayName);
				
			} catch (error) {
				console.error('사용자 정보 파싱 실패:', error);
				setUsername('사용자');
			}
		} else {
			setIsLoggedIn(false);
			setUsername('');
			setWishlistCount(0);
			setCartCount(0);
		}
	};

	const updateCartCount = async () => {
		const token = localStorage.getItem('access_token');
		
		if (token) {
			try {
				const response = await axios.get(
					'https://api.weartoday.kr/api/products/cart/count/',
					{
						headers: {
							'Authorization': `Bearer ${token}`
						}
					}
				);
				
				const count = response.data.count || 0;
				setCartCount(count);
				
			} catch (error) {
				console.error('장바구니 개수 조회 실패:', error);
				setCartCount(0);
			}
		} else {
			const cart = JSON.parse(localStorage.getItem('cart') || '[]');
			const total = cart.reduce((sum, item) => sum + item.quantity, 0);
			setCartCount(total);
		}
	};

	const updateWishlistCount = async () => {
		const token = localStorage.getItem('access_token');
		
		if (!token) {
			setWishlistCount(0);
			return;
		}

		try {
			const response = await axios.get(
				'https://api.weartoday.kr/api/products/wishlist/',
				{
					headers: {
						'Authorization': `Bearer ${token}`
					}
				}
			);
			
			let count = 0;
			if (Array.isArray(response.data)) {
				count = response.data.length;
			} else if (response.data && response.data.results) {
				count = response.data.results.length;
			} else if (response.data && response.data.count !== undefined) {
				count = response.data.count;
			}
			
			setWishlistCount(count);
			
		} catch (error) {
			if (error.response?.status === 401) {
				localStorage.removeItem('access_token');
				localStorage.removeItem('refresh_token');
				localStorage.removeItem('user');
				setIsLoggedIn(false);
				setUsername('');
				setWishlistCount(0);
				setCartCount(0);
			} else {
				console.error('찜목록 로딩 실패:', error);
			}
			setWishlistCount(0);
		}
	};

	const handleLogout = () => {
		localStorage.removeItem('access_token');
		localStorage.removeItem('refresh_token');
		localStorage.removeItem('user');
		setIsLoggedIn(false);
		setUsername('');
		setWishlistCount(0);
		setCartCount(0);
		alert('로그아웃 되었습니다.');
		window.dispatchEvent(new Event('loginStateChanged'));
		navigate('/');
	};

	// ✅ 검색 핸들러 (모바일 개선)
	const handleSearch = (e) => {
		e.preventDefault();
		
		// 모바일 키보드 닫기
		if (document.activeElement) {
			document.activeElement.blur();
		}
		
		if (searchTerm.trim()) {
			navigate(`/?search=${encodeURIComponent(searchTerm.trim())}`);
		} else {
			navigate('/');
		}
		
		// 약간의 딜레이 후 스크롤 (네비게이션 완료 대기)
		setTimeout(() => {
			scrollToTop();
		}, 100);
	};

	// ✅ 로고 클릭 핸들러 (모바일 개선)
	const handleLogoClick = (e) => {
		e.preventDefault();
		e.stopPropagation(); // 이벤트 버블링 방지
		
		console.log('🏠 로고 클릭됨');
		setShowSituationMode(false);
		setSearchTerm('');
		
		// 이벤트 발생
		window.dispatchEvent(new CustomEvent('logoClick'));
		
		// 네비게이션
		navigate('/', { replace: true });
		
		// ✅ 모바일 호환 상단 이동 (딜레이 적용)
		setTimeout(() => {
			scrollToTop();
			console.log('📍 상단 이동 실행됨');
		}, 50);
		
		// 추가 보험 (iOS Safari 대응)
		requestAnimationFrame(() => {
			scrollToTop();
		});
	};

	// ✅ 카테고리 클릭 핸들러 (모바일 개선)
	const handleCategoryClick = (categoryName) => {
		console.log('📂 카테고리 클릭:', categoryName);
		setShowSituationMode(false);
		
		// 전체 카테고리 처리
		if (categoryName === '전체') {
			navigate('/');
		} else {
			navigate(`/?category=${encodeURIComponent(categoryName)}`);
		}
		
		// 이벤트 발생
		window.dispatchEvent(new CustomEvent('headerCategoryClick', { 
			detail: categoryName 
		}));
		
		// ✅ 모바일 호환 상단 이동
		setTimeout(() => {
			scrollToTop();
		}, 50);
		
		requestAnimationFrame(() => {
			scrollToTop();
		});
	};

	// ✅ 오늘 뭐 입지 핸들러 (모바일 개선)
	const handleSituationClick = (e) => {
		e.preventDefault();
		e.stopPropagation();
		
		console.log('🤔 오늘 뭐 입지? 버튼 클릭됨');
		
		setShowSituationMode(true);
		
		navigate('/', {
			state: {
				situationMode: true,
				fromButton: true
			}
		});
		
		console.log('✅ navigate 실행 완료');
		
		// ✅ 모바일 호환 상단 이동
		setTimeout(() => {
			scrollToTop();
		}, 50);
		
		requestAnimationFrame(() => {
			scrollToTop();
		});
	};

	// ✅ BEST/NEW 클릭 핸들러 (모바일 개선)
	const handleNavClick = (path) => {
		navigate(path);
		
		setTimeout(() => {
			scrollToTop();
		}, 50);
	};

	return (
		<header className="header">
			<div className="header-top">
				<div className="logo-section">
					{/* ✅ 로고에 터치 이벤트 추가 */}
					<h1 
						className="logo" 
						onClick={handleLogoClick}
						onTouchEnd={(e) => {
							e.preventDefault();
							handleLogoClick(e);
						}}
						style={{ cursor: 'pointer', userSelect: 'none', WebkitTapHighlightColor: 'transparent' }}
					>
						wearToday Mall
					</h1>
					{/* ✅ 오늘 뭐 입지 버튼에 터치 이벤트 추가 */}
					<button 
						onClick={handleSituationClick}
						onTouchEnd={(e) => {
							e.preventDefault();
							handleSituationClick(e);
						}}
						className={`situation-btn ${showSituationMode ? 'active' : ''}`}
						style={{ WebkitTapHighlightColor: 'transparent' }}
					>
						🤔 오늘 뭐 입지?
					</button>
				</div>

				<div className="user-menu">
					{isLoggedIn ? (
						<>
							<span className="username">
								안녕하세요, {username} 님
							</span>
							
							<button 
								className="menu-link mypage-btn"
								onClick={() => navigate('/mypage')}
							>
								My Page
							</button>
							
							<Link to="/wishlist" className="menu-link wishlist-link">
								찜하기 
								{wishlistCount > 0 && (
									<span className="badge">{wishlistCount}</span>
								)}
							</Link>
							
							<Link to="/cart" className="menu-link cart-link">
								장바구니 
								{cartCount > 0 && (
									<span className="badge">{cartCount}</span>
								)}
							</Link>
							
							<button onClick={handleLogout} className="menu-link logout-btn">
								로그아웃
							</button>
						</>
					) : (
						<>
							<Link to="/login" className="menu-link">로그인</Link>
							<Link to="/register" className="menu-link">회원가입</Link>
						</>
					)}
				</div>
			</div>

			<div className="header-bottom">
				<nav className="nav">
					{/* ✅ 카테고리 버튼에 터치 이벤트 추가 */}
					<button 
						onClick={() => handleCategoryClick('전체')}
						onTouchEnd={(e) => {
							e.preventDefault();
							handleCategoryClick('전체');
						}}
						className="nav-btn"
						type="button"
						style={{ WebkitTapHighlightColor: 'transparent' }}
					>
						🏠 전체
					</button>
					<button 
						onClick={() => handleCategoryClick('여성패션')}
						onTouchEnd={(e) => {
							e.preventDefault();
							handleCategoryClick('여성패션');
						}}
						className="nav-btn"
						type="button"
						style={{ WebkitTapHighlightColor: 'transparent' }}
					>
						👗 여성패션
					</button>
					<button 
						onClick={() => handleCategoryClick('남성패션')}
						onTouchEnd={(e) => {
							e.preventDefault();
							handleCategoryClick('남성패션');
						}}
						className="nav-btn"
						type="button"
						style={{ WebkitTapHighlightColor: 'transparent' }}
					>
						👔 남성패션
					</button>
					<button 
						onClick={() => handleCategoryClick('액세서리/신발')}
						onTouchEnd={(e) => {
							e.preventDefault();
							handleCategoryClick('액세서리/신발');
						}}
						className="nav-btn"
						type="button"
						style={{ WebkitTapHighlightColor: 'transparent' }}
					>
						👜 액세서리/신발
					</button>
					<button 
						onClick={() => handleNavClick('/best')}
						onTouchEnd={(e) => {
							e.preventDefault();
							handleNavClick('/best');
						}}
						className="nav-btn"
						type="button"
						style={{ WebkitTapHighlightColor: 'transparent' }}
					>
						🔥 BEST
					</button>
					<button 
						onClick={() => handleNavClick('/new')}
						onTouchEnd={(e) => {
							e.preventDefault();
							handleNavClick('/new');
						}}
						className="nav-btn"
						type="button"
						style={{ WebkitTapHighlightColor: 'transparent' }}
					>
						✨ NEW
					</button>
				</nav>

				<form className="search-form" onSubmit={handleSearch}>
					<input
						type="text"
						className="search-input"
						placeholder="상품명을 검색하세요..."
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
					/>
					<button type="submit" className="search-btn">🔍</button>
				</form>
			</div>
		</header>
	);
}

export default Header;
